import React from "react";
import cn from "classnames";

import { useSelector } from "react-redux";

import { AudioContext } from "src/contexts";
import { betsUndoThunk, betsAddedX2Thunk, betsRepeatSubmitThunk } from "src/thunks";
import { createGetIsAnyGame, getLoading, getNextId, getTheme } from "src/selectors";
import { createGetIsDoubleDisabled, createGetIsRepeatDisabled } from "./selectors";
import { getDataTestId } from "src/utils";
import { ControlProps } from "./types";
import { useAppDispatch, useContent } from "src/hooks";

import style from "./style.module.scss";

export const useControl = (props: ControlProps) => {
  const content = useContent();

  const dispatch = useAppDispatch();

  const nextId = useSelector(getNextId);
  const theme = useSelector(getTheme);

  const isNextTechBreak = useSelector(createGetIsAnyGame([nextId], ["tech_break"]));
  const isDoubleDisabled = useSelector(createGetIsDoubleDisabled(nextId));
  const isRepeatDisabled = useSelector(createGetIsRepeatDisabled(nextId));
  const loading = useSelector(getLoading);
  const isUndoDisabled = isDoubleDisabled;

  const { play } = React.useContext(AudioContext);

  const text = React.useMemo(() => {
    switch (props.type) {
      case "double":
        return "УДВОИТЬ";
      case "repeat":
        return "ПОВТОРИТЬ";
      case "undo":
        return "ОТМЕНИТЬ";
      default:
        return false;
    }
  }, [props.type]);

  const testId = React.useMemo(() => {
    switch (props.type) {
      case "double":
        return "button-x2";
      case "repeat":
        return "button-repeat";
      case "undo":
        return "button-undo";
      default:
        return "";
    }
  }, [props.type]);

  const disabled = React.useMemo(() => {
    switch (props.type) {
      case "double":
        return isDoubleDisabled || isNextTechBreak;
      case "repeat":
        return isRepeatDisabled || isNextTechBreak || loading;
      case "undo":
        return isUndoDisabled || isNextTechBreak;
      default:
        return false;
    }
  }, [
    isDoubleDisabled,
    isNextTechBreak,
    isRepeatDisabled,
    isUndoDisabled,
    loading,
    props.type,
  ]);

  const onClick = React.useCallback(async () => {
    play("control");
    switch (props.type) {
      case "double":
        dispatch(betsAddedX2Thunk());
        break;
      case "repeat": {
        const result = await dispatch(betsRepeatSubmitThunk());
        if (result && !result.hasError) {
          play("success");
        } else if (result?.hasError) {
          play("error");
        }
        break;
      }
      case "undo":
        dispatch(betsUndoThunk());
        break;
      default:
        break;
    }
  }, [dispatch, play, props.type]);

  const getButtonProps = React.useCallback(
    () => ({
      className: cn(
        style[content],
        style[theme],
        style[props.type],
        props.className && style[props.className]
      ),
      disabled,
      onClick,
      ...getDataTestId(testId),
    }),
    [content, disabled, onClick, props.className, props.type, testId, theme]
  );

  const values = React.useMemo(
    () => ({
      text,
    }),
    [text]
  );

  return { getButtonProps, values };
};
